if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
      };

      this.mql = window.matchMedia('(min-width: 750px)');

      // If there is no viewer at all, nothing to do.
      if (!this.elements.viewer) return;

      // Mobile Safari fix: ensure tapping the video poster actually loads/plays the media
      this.elements.viewer.addEventListener('click', (event) => {
        const poster = event.target.closest(
          '.deferred-media__poster, .deferred-media__poster-button, .deferred-media'
        );
        if (!poster) return;

        const deferred = poster.classList.contains('deferred-media')
          ? poster
          : poster.closest('.deferred-media');

        if (!deferred) return;

        // Prevent slider swipe handlers from swallowing the tap
        event.preventDefault();
        event.stopPropagation();

        deferred.loadContent(false);
      });

      // When the main slider changes (eg. swiped on mobile), sync thumbnail state
      this.elements.viewer.addEventListener(
        'slideChanged',
        debounce(this.onSlideChanged.bind(this), 500)
      );

      // Wire up thumbnail clicks if thumbnails exist
      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          if (!btn) return;

          btn.addEventListener(
            'click',
            this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
          );
        });

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      // Ensure we have a known "active" media on load
      const initiallyActive =
        this.elements.viewer.querySelector('.product__media-item.is-active') ||
        this.elements.viewer.querySelector('[data-media-id]');

      if (initiallyActive) {
        const mediaId = initiallyActive.dataset.mediaId;
        if (mediaId) {
          this.setActiveMedia(mediaId, false);
        }
      }

      // Make this gallery the active one immediately, and refresh on hover
      MediaGallery.activeInstance = this;

      this.elements.viewer.addEventListener('mouseenter', () => {
        MediaGallery.activeInstance = this;
      });
    }

    onSlideChanged(event) {
      if (!this.elements.thumbnails) return;

      const thumbnail = this.elements.thumbnails.querySelector(
        `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
      );
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia =
        this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
        this.elements.viewer.querySelector('[data-media-id]');
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        if (activeMedia.parentElement.firstChild !== activeMedia) {
          activeMedia.parentElement.prepend(activeMedia);
        }

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );
          if (
            activeThumbnail &&
            activeThumbnail.parentElement.firstChild !== activeThumbnail
          ) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }

        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();

      window.setTimeout(() => {
        if (!this.mql.matches || this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }

        const activeMediaRect = activeMedia.getBoundingClientRect();
        if (activeMediaRect.top > -0.5) return;

        const top = activeMediaRect.top + window.scrollY;
        window.scrollTo({ top, behavior: 'smooth' });
      });

      this.playActiveMedia(activeMedia);

      if (this.elements.thumbnails) {
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails
        .querySelectorAll('button')
        .forEach((element) => element.removeAttribute('aria-current'));

      const btn = thumbnail.querySelector('button');
      if (!btn) return;

      btn.setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;
      if (this.elements.thumbnails.slider) {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;

      image.onload = () => {
        this.elements.liveRegion?.setAttribute('aria-hidden', false);
        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace('[index]', position);
        }
        setTimeout(() => {
          this.elements.liveRegion?.setAttribute('aria-hidden', true);
        }, 2000);
      };

      // Trigger load event
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia?.();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach((slide) =>
        slide.setAttribute('role', 'presentation')
      );
    }
  }

  MediaGallery.activeInstance = null;

  customElements.define('media-gallery', MediaGallery);

  // Global arrow-key handler (works with or without thumbnails)
  if (!window.__mediaGalleryArrowsBound) {
    window.__mediaGalleryArrowsBound = true;

    window.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      const instance = MediaGallery.activeInstance;
      if (!instance || !instance.elements.viewer) return;

      event.preventDefault();

      // Prefer thumbnails when available
      const thumbRoot = instance.elements.thumbnails;
      const thumbNodes = thumbRoot
        ? Array.from(thumbRoot.querySelectorAll('[data-target]'))
        : [];

      if (thumbNodes.length) {
        const currentThumb =
          thumbRoot
            .querySelector('button[aria-current="true"]')
            ?.closest('[data-target]') || thumbNodes[0];

        let index = thumbNodes.indexOf(currentThumb);
        index = event.key === 'ArrowRight' ? index + 1 : index - 1;

        if (index < 0) index = thumbNodes.length - 1;
        if (index >= thumbNodes.length) index = 0;

        const nextThumb = thumbNodes[index];
        instance.setActiveMedia(nextThumb.dataset.target, false);
        return;
      }

      // Fallback: use main slider items if no thumbnails
      const slides = instance.elements.viewer.sliderItems
        ? Array.from(instance.elements.viewer.sliderItems)
        : Array.from(instance.elements.viewer.querySelectorAll('[data-media-id]'));

      if (!slides.length) return;

      const currentSlide =
        slides.find((s) => s.classList.contains('is-active')) || slides[0];

      let index = slides.indexOf(currentSlide);
      index = event.key === 'ArrowRight' ? index + 1 : index - 1;

      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;

      const nextSlide = slides[index];
      const mediaId = nextSlide.dataset.mediaId;
      if (mediaId) instance.setActiveMedia(mediaId, false);
    });
  }
}
